import { PROMOTION_TYPES, SearchModes } from '@/domainLogic/index.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { calculateNights } from '@/app/components/search/utils/buildDealCards/utils.ts';
import {
  decorateMealPlan,
  decorateMealPlanMoreDescriptive,
  decorateNightsStay,
  decorateWaitingNightsStay,
} from '@/app/components/search/components/Card/utils/getDecorators.ts';
import { hasPromotion } from '@/app/components/search/components/Card/utils/getPromotionsUtils.ts';
import type {
  GetFeaturesInnerParams,
  GetFeaturesParams,
} from '@/app/components/search/components/Card/Details/Features/getFeatures/types.ts';
import type { Feature } from '@/app/components/search/components/Card/utils/facilitiesTypes.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import {
  getShortsDatesText,
  getShortsDatesWithMonthText,
} from '@/app/components/search/components/Card/Details/Features/getFeatures/utils/geDatesText.ts';
import { getAdultsChildrenCount } from '@/utils/peopleUtils.ts';
import { freeCancellationWithdrawn } from '@/app/common/filters/core/filterFlags.ts';

const isDatesOnCardDifferent = (searchParams?: SearchParams, dateFromCard?: string, dateToCard?: string) => {
  const dateToUrl = searchParams?.[URLParameters.DATE_TO];
  const dateFromUrl = searchParams?.[URLParameters.DATE_FROM];

  return dateFromUrl !== dateFromCard || dateToUrl !== dateToCard;
};

export const getParams = ({
  commonData,
  localization,
  dealCard,
  t,
  promotionTypes,
  searchParams,
  isWaiting,
  isLoading,
  isOverlay,
  curiosityParams,
  searchInfoData,
  searchInfoDetailData,
  hasPreselectedFlightLink,
  transportData,
  isClient,
}: GetFeaturesParams): GetFeaturesInnerParams => {
  const imageBasePath = localization?.imageBasePath;
  const searchMode = searchInfoData?.searchMode;
  const isLiveSearch = searchInfoData?.isLiveSearch;
  const isHO = searchMode === SearchModes.HO;
  const isDP = searchMode === SearchModes.DP;
  const isTH = searchMode === SearchModes.TH;
  const isMobile = commonData.isMobileDevice;
  const product = dealCard?.product;
  const soldOut = !!dealCard?.product?.dates?.soldOut;
  const alternative = dealCard?.product?.dates?.alternative;
  const distanceUsp = dealCard?.product?.geo?.distanceUsp;
  const cancellation = dealCard?.product?.rate?.features?.cancellation;
  const hideFreeCancellation = freeCancellationWithdrawn(localization?.configValuesApp);
  const mealPlan = dealCard?.product?.rate?.features?.mealPlan;
  const accommodation = dealCard?.product?.accommodation;
  const features: Feature[] = [];
  const isDPFlexible = isDP && !isLiveSearch;
  const opxFlexibleEnabled = localization?.configValuesApp?.opxFlexible;
  const flexibleAlternativeDates = localization?.configValuesApp?.flexibleAlternativeDatesEnabled;
  const showDescriptiveMealPlans = isHO || isDPFlexible;
  const originName = dealCard?.product?.geo?.originName;
  const hasCustomCheckinCheckoutApplied = isDP && !!searchParams?.[URLParameters.CUSTOM_CHECKIN_CHECKOUT];
  const alternativeDeals = dealCard?.product?.dates?.alternativeDeals;
  // const hasOvernightFlight = searchParams?.[URLParameters.OVERNIGHT_FLIGHTS];
  // const showLoadingState = !!((hasCustomCheckinCheckoutEnabled && isWaiting) || (hasOvernightFlight && isWaiting));
  const cardCheckin = dealCard?.product?.dates?.checkin;
  const cardCheckout = dealCard?.product?.dates?.checkout;
  // const cardDateFrom = dealCard?.product?.dates?.dateFrom;
  // const cardDateTo = dealCard?.product?.dates?.dateTo;
  const nightsCount =
    dealCard?.product?.dates?.accNights ||
    dealCard?.product?.dates?.nights ||
    calculateNights(cardCheckin, cardCheckout);
  // const dateFrom = isWaiting ? searchParams?.[URLParameters.DATE_FROM] : cardDateFrom;
  // const dateTo = isWaiting ? searchParams?.[URLParameters.DATE_TO] : cardDateTo;
  const nightsText = isWaiting
    ? decorateWaitingNightsStay(searchParams?.[URLParameters.DATE_FROM], searchParams?.[URLParameters.DATE_TO], t)
    : decorateNightsStay(nightsCount, t);
  const datesText = getShortsDatesText({ isWaiting, dealCard, localization, searchParams });
  const datesTextWithDay = getShortsDatesWithMonthText({ isWaiting, dealCard, localization, searchParams });
  const showFlexibleAlternativeDates = !!(flexibleAlternativeDates && alternativeDeals && !isLiveSearch && !isWaiting);
  const meal = showDescriptiveMealPlans ? decorateMealPlanMoreDescriptive(mealPlan, t) : decorateMealPlan(mealPlan, t);
  const hasPromotionTypes = promotionTypes && promotionTypes.length > 0;
  const hasAlternativeDates = Array.isArray(alternative) && alternative.length > 0;
  const hasFranceUSP = hasPromotion(PROMOTION_TYPES.TO_FR, promotionTypes);
  const hasTravelExtraPack = hasPromotion(PROMOTION_TYPES.TRAVEL_EXTRA_PACK, promotionTypes);
  const hasDepositAmount = hasPromotion(PROMOTION_TYPES.DEPOSIT_AMOUNT, promotionTypes);
  const hasPackageSavings = hasPromotion(PROMOTION_TYPES.PACKAGE_SAVINGS, promotionTypes);
  const packageSavingsUspEnabled = localization?.configValuesApp?.packageSavingsUspEnabled;
  const isOpx = !!product?.rate?.flags?.opx;
  const isOpxFlexible = isOpx && opxFlexibleEnabled;
  const areDatesDifferentFromCSW = isDatesOnCardDifferent(searchParams, cardCheckin, cardCheckout);
  const areTripDatesDifferentFromCSW = areDatesDifferentFromCSW && isOpxFlexible;
  const areHotelDatesDifferentFromCSW = areDatesDifferentFromCSW && !isOpxFlexible;
  const hasDirectFlight = !!dealCard?.product?.transport?.direct;
  const isPartialStay = areHotelDatesDifferentFromCSW && isLiveSearch && hasCustomCheckinCheckoutApplied;
  const hasOpxFlexDates = areTripDatesDifferentFromCSW && isLiveSearch && opxFlexibleEnabled;
  const adultsChildrenCount = getAdultsChildrenCount(searchParams);

  return {
    accommodation,
    cancellation,
    hideFreeCancellation,
    commonData,
    curiosityParams,
    datesText,
    datesTextWithDay,
    dealCard,
    soldOut,
    distanceUsp,
    features,
    hasAlternativeDates,
    hasCustomCheckinCheckoutApplied,
    hasFranceUSP,
    hasPackageSavings,
    hasPreselectedFlightLink,
    hasPromotionTypes,
    hasTravelExtraPack,
    imageBasePath,
    isClient,
    isHO,
    isDP,
    isTH,
    isOpx,
    isLiveSearch,
    isFlexible: !isLiveSearch,
    isLoading,
    isMobile,
    isOverlay,
    isWaiting,
    localization,
    meal,
    nightsCount,
    nightsText,
    packageSavingsUspEnabled,
    product,
    promotionTypes,
    searchInfoData,
    searchInfoDetailData,
    searchParams,
    showFlexibleAlternativeDates,
    t,
    transportData,
    hasDepositAmount,
    isDPFlexible,
    areTripDatesDifferentFromCSW,
    areHotelDatesDifferentFromCSW,
    opxFlexibleEnabled,
    originName,
    hasDirectFlight,
    isPartialStay,
    hasOpxFlexDates,
    adultsChildrenCount,
  };
};
